import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading, inr } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { IndianRupee, Plus, Trash2, Calculator, Save } from "lucide-react";
import { LaunchPricingPolicyPanel } from "../../components/admin/LaunchPricingPolicyPanel";

const Section = ({ title, desc, children }) => (
  <div className="bg-card border rounded-xl p-5 mb-4">
    <div className="mb-4"><h3 className="font-semibold">{title}</h3>{desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}</div>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div><Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>{children}</div>
);

export default function AdminMonetization() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewN, setPreviewN] = useState(150);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/admin/monetization").then((response) => setS(response.data));
    api.get("/admin/monetization/history").then((response) => setHistory(response.data));
  }, []);
  useEffect(() => {
    if (!s) return;
    const t = setTimeout(() => api.get(`/admin/monetization/preview?members=${previewN || 0}`).then((r) => setPreview(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [previewN, s]);

  const set = (path, val) => {
    setS((prev) => {
      const next = structuredClone(prev);
      let o = next; const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = val;
      return next;
    });
  };
  const num = (v) => (v === "" || v === null ? "" : Number(v));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/monetization", {
        currency: s.currency, society: s.society, resident_portal: s.resident_portal,
        vendor: s.vendor, commission: s.commission, expert: s.expert,
        pricing_policy: s.pricing_policy,
      });
      const historyResponse = await api.get("/admin/monetization/history");
      setHistory(historyResponse.data);
      toast.success("Monetization settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <Loading />;

  const slabRow = (arr, i, key, valueKey) => (
    <div key={i} className="flex items-center gap-2 mb-2" data-testid={`${key}-row-${i}`}>
      <Input type="number" className="h-9 w-24" value={arr[i].from} onChange={(e) => set(`${key}.${i}.from`, num(e.target.value))} placeholder="From" />
      <span className="text-muted-foreground text-sm">to</span>
      <Input type="number" className="h-9 w-24" value={arr[i].to ?? ""} onChange={(e) => set(`${key}.${i}.to`, e.target.value === "" ? null : num(e.target.value))} placeholder="∞" />
      <span className="text-muted-foreground text-sm">@</span>
      <Input type="number" className="h-9 w-24" value={arr[i][valueKey]} onChange={(e) => set(`${key}.${i}.${valueKey}`, num(e.target.value))} />
      <span className="text-muted-foreground text-sm">{valueKey === "pct" ? "%" : "₹"}</span>
      <Button size="icon" variant="ghost" className="text-destructive h-9 w-9" onClick={() => set(key, arr.filter((_, j) => j !== i))} data-testid={`${key}-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );

  return (
    <div>
      <PageHeader title="Monetization Settings" subtitle="Configure all commercial values across the four revenue pillars. Nothing here is hard-coded.">
        <Button onClick={save} disabled={saving} className="rounded-full gap-2" data-testid="monetization-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}</Button>
      </PageHeader>

      <Tabs defaultValue="society">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="society" data-testid="mon-tab-society">Society ERP</TabsTrigger>
          <TabsTrigger value="vendor" data-testid="mon-tab-vendor">Vendor</TabsTrigger>
          <TabsTrigger value="commission" data-testid="mon-tab-commission">Service Commission</TabsTrigger>
          <TabsTrigger value="expert" data-testid="mon-tab-expert">Expert Consultation</TabsTrigger>
        </TabsList>

        <TabsContent value="society">
          <Section
            title="Legacy Subscription Slabs"
            desc="Existing subscription pricing. Budget Launch quotations use the versioned policy below."
          >
            {s.society.slabs.map((_, i) => slabRow(s.society.slabs, i, "society.slabs", "rate"))}
            <Button size="sm" variant="outline" className="rounded-full gap-1.5 mt-1" onClick={() => set("society.slabs", [...s.society.slabs, { from: (s.society.slabs.at(-1)?.to || 0) + 1, to: null, rate: 10 }])} data-testid="add-society-slab"><Plus className="w-3.5 h-3.5" /> Add Slab</Button>
            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              <Field label="Minimum monthly (₹)"><Input type="number" className="h-9" value={s.society.min_monthly} onChange={(e) => set("society.min_monthly", num(e.target.value))} data-testid="society-min" /></Field>
              <Field label="Free trial (days)"><Input type="number" className="h-9" value={s.society.trial_days} onChange={(e) => set("society.trial_days", num(e.target.value))} data-testid="society-trial" /></Field>
              <Field label="Annual discount (%)"><Input type="number" className="h-9" value={s.society.annual_discount_pct} onChange={(e) => set("society.annual_discount_pct", num(e.target.value))} /></Field>
            </div>
            <div className="flex items-center gap-3 mt-4"><Switch checked={s.society.gst_enabled} onCheckedChange={(v) => set("society.gst_enabled", v)} data-testid="society-gst" /><span className="text-sm">Apply GST</span>{s.society.gst_enabled && <Input type="number" className="h-9 w-24" value={s.society.gst_pct} onChange={(e) => set("society.gst_pct", num(e.target.value))} />}{s.society.gst_enabled && <span className="text-sm text-muted-foreground">%</span>}</div>
          </Section>

          <Section title="Resident Digital Portal (add-on)" desc="Optional per-member add-on for individual resident logins.">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Rate per member / month (₹)"><Input type="number" className="h-9" value={s.resident_portal.rate_per_member} onChange={(e) => set("resident_portal.rate_per_member", num(e.target.value))} data-testid="resident-rate" /></Field>
              <div className="flex items-center gap-3 pt-6"><Switch checked={s.resident_portal.enabled_by_default} onCheckedChange={(v) => set("resident_portal.enabled_by_default", v)} /><span className="text-sm">Enabled by default for new societies</span></div>
            </div>
          </Section>

          <LaunchPricingPolicyPanel
            history={history.filter((item) => item.snapshot?.pricing_policy)}
            policy={s.pricing_policy}
            setValue={set}
          />

          <Section title="Live Calculator" desc="Preview the progressive ERP subscription for a given member count.">
            <div className="flex items-center gap-3">
              <Calculator className="w-5 h-5 text-primary" />
              <Field label="Members"><Input type="number" className="h-9 w-32" value={previewN} onChange={(e) => setPreviewN(num(e.target.value))} data-testid="preview-members" /></Field>
            </div>
            {preview && (
              <div className="mt-4 border rounded-lg overflow-hidden text-sm" data-testid="preview-result">
                {preview.breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between px-4 py-2 border-b bg-muted/30"><span>{b.from}–{b.to ?? "∞"} · {b.members} members × ₹{b.rate}</span><span className="font-medium">{inr(b.amount)}</span></div>
                ))}
                <div className="flex justify-between px-4 py-2.5 font-semibold bg-primary/5"><span>ERP subscription / month</span><span data-testid="preview-total">{inr(preview.effective_monthly)}</span></div>
                <div className="flex justify-between px-4 py-2 text-muted-foreground"><span>+ Resident portal (optional)</span><span>{inr(preview.resident_portal_monthly)}</span></div>
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="vendor">
          <Section title="Vendor Subscription" desc="Default vendor network subscription. Super Admin can waive per vendor in Phase 2.">
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Monthly (₹)"><Input type="number" className="h-9" value={s.vendor.monthly} onChange={(e) => set("vendor.monthly", num(e.target.value))} data-testid="vendor-monthly" /></Field>
              <Field label="Annual (₹)"><Input type="number" className="h-9" value={s.vendor.annual} onChange={(e) => set("vendor.annual", num(e.target.value))} /></Field>
              <Field label="Free trial (days)"><Input type="number" className="h-9" value={s.vendor.trial_days} onChange={(e) => set("vendor.trial_days", num(e.target.value))} /></Field>
            </div>
            <div className="flex items-center gap-3 mt-4"><Switch checked={s.vendor.gst_enabled} onCheckedChange={(v) => set("vendor.gst_enabled", v)} /><span className="text-sm">Apply GST</span>{s.vendor.gst_enabled && <Input type="number" className="h-9 w-24" value={s.vendor.gst_pct} onChange={(e) => set("vendor.gst_pct", num(e.target.value))} />}</div>
          </Section>
        </TabsContent>

        <TabsContent value="commission">
          <Section title="Default Commission" desc="Fallback platform/service commission when no slab or override matches.">
            <Field label="Default commission (%)"><Input type="number" className="h-9 w-32" value={s.commission.default_pct} onChange={(e) => set("commission.default_pct", num(e.target.value))} data-testid="commission-default" /></Field>
          </Section>
          <Section title="Project-Value Commission Slabs" desc="Commission % by project value (e.g. small jobs 8%, medium 6%, large 5%).">
            {s.commission.project_value_slabs.map((_, i) => slabRow(s.commission.project_value_slabs, i, "commission.project_value_slabs", "pct"))}
            <Button size="sm" variant="outline" className="rounded-full gap-1.5 mt-1" onClick={() => set("commission.project_value_slabs", [...s.commission.project_value_slabs, { from: (s.commission.project_value_slabs.at(-1)?.to || 0) + 1, to: null, pct: 5 }])} data-testid="add-commission-slab"><Plus className="w-3.5 h-3.5" /> Add Slab</Button>
            <div className="flex items-center gap-3 mt-4"><Switch checked={s.commission.gst_enabled} onCheckedChange={(v) => set("commission.gst_enabled", v)} /><span className="text-sm">Apply GST on commission</span></div>
          </Section>
        </TabsContent>

        <TabsContent value="expert">
          <Section title="Consultation Charging" desc="Free introductory minutes then per-minute billing with configurable revenue share.">
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Free minutes"><Input type="number" className="h-9" value={s.expert.free_minutes} onChange={(e) => set("expert.free_minutes", num(e.target.value))} data-testid="expert-free" /></Field>
              <Field label="Default rate / minute (₹)"><Input type="number" className="h-9" value={s.expert.default_rate_per_min} onChange={(e) => set("expert.default_rate_per_min", num(e.target.value))} data-testid="expert-rate" /></Field>
              <Field label="Minimum charge (₹)"><Input type="number" className="h-9" value={s.expert.min_charge} onChange={(e) => set("expert.min_charge", num(e.target.value))} /></Field>
              <Field label="Expert share (%)"><Input type="number" className="h-9" value={s.expert.expert_share_pct} onChange={(e) => { const v = num(e.target.value); set("expert.expert_share_pct", v); set("expert.platform_share_pct", v === "" ? "" : 100 - v); }} data-testid="expert-share" /></Field>
              <Field label="MySocietyMitra share (%)"><Input type="number" className="h-9" value={s.expert.platform_share_pct} onChange={(e) => { const v = num(e.target.value); set("expert.platform_share_pct", v); set("expert.expert_share_pct", v === "" ? "" : 100 - v); }} /></Field>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Example: 20 paid min × ₹{s.expert.default_rate_per_min} = {inr(20 * s.expert.default_rate_per_min)} → Expert {inr(20 * s.expert.default_rate_per_min * s.expert.expert_share_pct / 100)}, Platform {inr(20 * s.expert.default_rate_per_min * s.expert.platform_share_pct / 100)}</p>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
